import {
    Box,
    IconButton,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";

type Props = {
    paginationModel: {
        page: number;
        pageSize: number;
    };
    onPageSizeChange: (pageSize: number) => void;
    onPageChange: (page: number) => void;
    rows: any[];
};

const ArrowIcon = ({
    rotate = false,
}: {
    rotate?: boolean;
}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        style={{
            transform: rotate
                ? "rotate(180deg)"
                : "none",
        }}
    >
        <path
            d="M8.58997 16.34L13.17 11.75L8.58997 7.16L9.99997 5.75L16 11.75L9.99997 17.75L8.58997 16.34Z"
            fill="#919EAB"
        />
    </svg>
);

export default function ConquestModeFooter({
    paginationModel,
    onPageSizeChange,
    onPageChange,
    rows,
}: Props) {
    const from =
        paginationModel.page *
            paginationModel.pageSize +
        1;

    const to = Math.min(
        from + paginationModel.pageSize - 1,
        rows.length
    );

    const pageCount = Math.ceil(
        rows.length / paginationModel.pageSize
    );

    const isFirstPage =
        paginationModel.page === 0;

    const isLastPage =
        paginationModel.page >= pageCount - 1;

    return (
        <Box
            sx={{
                display: "flex",
                height: "40px",
                minHeight: "40px",
                maxHeight: "40px",
                alignItems: "center",
                alignSelf: "stretch",
                justifyContent: "flex-end",
                px: "24px",
                backgroundColor: "#FAFAFA",
                borderTop: "none",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                }}
            >
                <Typography
                    sx={{
                        color: "#919EAB",
                        fontFamily: '"Public Sans"',
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "22px",
                        whiteSpace: "nowrap",
                    }}
                >
                    Rows per page:
                </Typography>

                <Select
                    value={paginationModel.pageSize}
                    onChange={(event) =>
                        onPageSizeChange(
                            Number(event.target.value)
                        )
                    }
                    IconComponent={(props) => (
                        <svg
                            {...props}
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                        >
                            <path
                                d="M4 6L8 10L12 6"
                                stroke="#919EAB"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    )}
                    sx={{
                        ml: "12px",
                        width: "70px",
                        height: "32px",
                        color: "#919EAB",
                        fontFamily: '"Public Sans"',
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "22px",
                        backgroundColor: "#FFFFFF",
                        borderRadius: "8px",

                        "& .MuiSelect-select": {
                            minHeight: "unset !important",
                            boxSizing: "border-box",
                            display: "flex",
                            alignItems: "center",
                            padding:
                                "5px 36px 5px 20px !important",
                            lineHeight: "22px",
                        },

                        "& .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid #DFE3E8",
                        },

                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#DFE3E8",
                        },

                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#DFE3E8",
                            borderWidth: "1px",
                        },

                        "& .MuiSelect-icon": {
                            width: "16px",
                            height: "16px",
                            right: "12px",
                            top: "50%",
                            marginTop: "-8px",
                        },
                    }}
                >
                    {[5, 10, 20, 50].map(
                        (size) => (
                            <MenuItem
                                key={size}
                                value={size}
                                sx={{
                                    fontFamily:
                                        '"Public Sans"',
                                    fontSize: "14px",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "22px",
                                }}
                            >
                                {size}
                            </MenuItem>
                        )
                    )}
                </Select>

                <Typography
                    sx={{
                        ml: "32px",
                        color: "#919EAB",
                        fontFamily: '"Public Sans"',
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "22px",
                        whiteSpace: "nowrap",
                    }}
                >
                    {from}–{to} of {rows.length}
                </Typography>

                <Box
                    sx={{
                        ml: "24px",
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                    }}
                >
                    <IconButton
                        disabled={isFirstPage}
                        onClick={() =>
                            onPageChange(
                                paginationModel.page - 1
                            )
                        }
                        sx={{
                            width: "24px",
                            height: "24px",
                            padding: 0,
                            color: "#919EAB",
                            opacity: isFirstPage
                                ? 0.5
                                : 1,
                            "&.Mui-disabled": {
                                opacity: 0.5,
                            },
                        }}
                    >
                        <ArrowIcon rotate />
                    </IconButton>

                    <IconButton
                        disabled={isLastPage}
                        onClick={() =>
                            onPageChange(
                                paginationModel.page + 1
                            )
                        }
                        sx={{
                            width: "24px",
                            height: "24px",
                            padding: 0,
                            color: "#919EAB",
                            opacity: isLastPage
                                ? 0.5
                                : 1,
                            "&.Mui-disabled": {
                                opacity: 0.5,
                            },
                        }}
                    >
                        <ArrowIcon />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}